import React, { useMemo } from "react";
import { useLang } from "@/context/LanguageContext";
import { ARABIC_LETTERS, LATIN_LETTERS, arabicInitial } from "@/lib/i18n";

/**
 * Alphabetical index. Groups countries by initial letter of the short name
 * in the active language (ar/en). Clicking a country selects it.
 */
export default function AlphabetMenu({ countries, onSelect, selectedCode }) {
  const { lang, t } = useLang();

  const grouped = useMemo(() => {
    const letters = lang === "ar" ? ARABIC_LETTERS : LATIN_LETTERS;
    const map = new Map();
    letters.forEach((l) => map.set(l, []));
    countries.forEach((c) => {
      const name = c.short_names[lang] || c.short_names.en;
      const initial = lang === "ar" ? arabicInitial(name) : name.charAt(0).toUpperCase();
      if (!map.has(initial)) map.set(initial, []);
      map.get(initial).push(c);
    });
    // Return only letters that have entries
    return Array.from(map.entries()).filter(([, arr]) => arr.length > 0).map(([l, arr]) => ({
      letter: l,
      countries: arr.sort((a, b) => (a.short_names[lang] || "").localeCompare(b.short_names[lang] || "")),
    }));
  }, [countries, lang]);

  return (
    <div className="rounded-2xl border border-stoneLine bg-card/70 backdrop-blur p-4 sm:p-6" data-testid="alphabet-menu">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="text-lg font-display font-bold text-ink">{t("alphabet_menu")}</h3>
        <span className="text-xs text-muted-foreground">{countries.length}</span>
      </div>
      <div className="sm:max-h-[420px] sm:overflow-y-auto thin-scroll sm:pe-1 space-y-3 sm:space-y-4">
        {grouped.map(({ letter, countries: group }) => (
          <div key={letter}>
            <div className="flex items-center gap-3 mb-1.5 sm:mb-2">
              <span className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-ochre/10 text-ochre font-display text-base sm:text-lg font-bold flex items-center justify-center">
                {letter}
              </span>
              <div className="flex-1 h-px bg-stoneLine" />
            </div>
            <ul className="grid grid-cols-2 gap-1 sm:block sm:space-y-1">
              {group.map((c) => {
                const active = selectedCode === c.code;
                return (
                  <li key={c.code}>
                    <button
                      onClick={() => onSelect(c.code)}
                      data-testid={`alpha-country-${c.code}`}
                      className={`w-full flex items-center gap-2 sm:gap-3 px-2.5 sm:px-3 py-2 rounded-lg text-start transition-colors border sm:border-0 ${
                        active ? "bg-ochre/10 text-ochre border-ochre/40" : "border-stoneLine/70 sm:border-transparent hover:bg-sandDeep/50 text-ink"
                      }`}
                    >
                      <img
                        src={c.flag_url}
                        alt=""
                        className="w-6 h-4 object-cover rounded-sm border border-stoneLine"
                      />
                      <span className="text-sm font-medium truncate">{c.short_names[lang]}</span>
                      {c.disputed && (
                        <span className="ms-auto hidden sm:inline text-[9px] uppercase tracking-widest text-ochre">
                          {t("disputed")}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
